import { getAuthoredOverview, renderAuthoredNarrativeBundle } from './authoredNarrativeRender';
import { shouldPublishAuthoredMethodology } from './authoredNarrativeLoader';

const CHART_ID = 'people_map';

export type PeopleMapVariable = 'income' | 'population_density' | 'political_lean';

export type PeopleMapMetrics = {
  variable: PeopleMapVariable;
  currentValue: number | null;
  californiaAverage: number | null;
  year: number | null;
  minValue?: number | null;
  maxValue?: number | null;
  medianValue?: number | null;
  tractCount?: number | null;
};

export function buildPeopleMapTokens(
  locationName: string,
  metrics: PeopleMapMetrics | null
): Record<string, string> {
  if (!metrics) {
    return { locationName, latestYear: '—', tractCount: '—' };
  }
  const year = metrics.year != null ? String(metrics.year) : '—';
  const tractCount =
    metrics.tractCount != null ? String(metrics.tractCount) : '—';
  if (metrics.variable === 'income') {
    const medianIncome =
      metrics.medianValue != null
        ? `$${Math.round(metrics.medianValue).toLocaleString()}`
        : metrics.currentValue != null
          ? `$${Math.round(metrics.currentValue).toLocaleString()}`
          : '—';
    const minIncome =
      metrics.minValue != null
        ? `$${Math.round(metrics.minValue).toLocaleString()}`
        : '—';
    const maxIncome =
      metrics.maxValue != null
        ? `$${Math.round(metrics.maxValue).toLocaleString()}`
        : '—';
    return {
      locationName,
      tractCount,
      medianIncome,
      minIncome,
      maxIncome,
      cityCountyContextSentence: '',
      incomeMetricLabel: 'median household income',
      distributionSentence: '',
    };
  }
  if (metrics.variable === 'population_density') {
    const medianBase =
      metrics.medianValue ??
      (metrics.currentValue != null ? metrics.currentValue : null);
    return {
      locationName,
      tractCount,
      medianDensity:
        medianBase != null ? Math.round(medianBase).toLocaleString() : '—',
      minDensity:
        metrics.minValue != null ? Math.round(metrics.minValue).toLocaleString() : '—',
      maxDensity:
        metrics.maxValue != null ? Math.round(metrics.maxValue).toLocaleString() : '—',
      areaUnit: 'square mile',
      distributionSentence: '',
    };
  }
  return {
    locationName,
    electionYear: year,
    minVoteMetric: metrics.minValue != null ? metrics.minValue.toFixed(1) : '—',
    maxVoteMetric: metrics.maxValue != null ? metrics.maxValue.toFixed(1) : '—',
    medianVoteMetric:
      metrics.medianValue != null
        ? metrics.medianValue.toFixed(1)
        : metrics.currentValue != null
          ? metrics.currentValue.toFixed(1)
          : '—',
    electionDefinitionSentence: '',
  };
}

export function buildPeopleMapAuthoredBundle(locationName: string, metrics: PeopleMapMetrics | null) {
  if (!metrics) {
    return renderAuthoredNarrativeBundle(CHART_ID, 'income', { locationName });
  }
  const viewId = metrics.variable;
  const tokens = buildPeopleMapTokens(locationName, metrics);
  const bundle = renderAuthoredNarrativeBundle(CHART_ID, viewId, tokens);
  if (viewId === 'political_lean') {
    bundle.methodology = undefined;
  } else if (!shouldPublishAuthoredMethodology(bundle.authoringStatus)) {
    bundle.methodology = undefined;
  }
  return bundle;
}

export function peopleMapOverviewDescription(variable: PeopleMapVariable, fallback: string): string {
  return getAuthoredOverview(CHART_ID, variable, fallback);
}
